import { bindShopifyCaptchaSubmit } from '@theme/topeak-shopify-captcha';

export const SUBMIT_SOURCE_KEY = 'topeak-newsletter-submit-source';

export const NewsletterSubmitSource = {
  POPUP: 'popup',
  FOOTER: 'footer',
  SHOP_ALL: 'shop-all',
};

let submitTrackingInitialized = false;
let pageFeedbackHandled = false;

/**
 * Records which newsletter form was submitted before the page reloads.
 */
export function initNewsletterSubmitTracking() {
  if (submitTrackingInitialized) return;

  submitTrackingInitialized = true;

  for (const form of document.querySelectorAll('.topeak-footer__newsletter-form, .topeak-shop-all-email-capture__form')) {
    if (!(form instanceof HTMLFormElement)) continue;

    bindShopifyCaptchaSubmit(form, () => {
      if (form.classList.contains('topeak-footer__newsletter-form')) {
        sessionStorage.setItem(SUBMIT_SOURCE_KEY, NewsletterSubmitSource.FOOTER);
        setFooterFormLoading(form);
        return;
      }

      if (form.classList.contains('topeak-shop-all-email-capture__form')) {
        sessionStorage.setItem(SUBMIT_SOURCE_KEY, NewsletterSubmitSource.SHOP_ALL);
      }
    });
  }
}

/**
 * @returns {string | null}
 */
export function peekSubmitSource() {
  try {
    return sessionStorage.getItem(SUBMIT_SOURCE_KEY);
  } catch {
    return null;
  }
}

/**
 * @returns {string | null}
 */
export function consumeSubmitSource() {
  try {
    const source = sessionStorage.getItem(SUBMIT_SOURCE_KEY);

    if (source) {
      sessionStorage.removeItem(SUBMIT_SOURCE_KEY);
    }

    return source;
  } catch {
    return null;
  }
}

/**
 * @returns {boolean}
 */
export function isCustomerPosted() {
  return new URLSearchParams(window.location.search).has('customer_posted');
}

/**
 * @param {string | null} source
 * @returns {boolean}
 */
export function shouldShowPopupSuccessForSource(source) {
  return (
    source === NewsletterSubmitSource.POPUP || source === NewsletterSubmitSource.FOOTER
  );
}

/**
 * @param {HTMLFormElement} form
 */
function setFooterFormLoading(form) {
  const submit = form.querySelector('.topeak-footer__newsletter-button');
  const input = form.querySelector('.topeak-footer__newsletter-input');

  if (!(submit instanceof HTMLButtonElement)) return;

  const loadingLabel = submit.dataset.loadingLabel || 'Subscribing...';

  submit.setAttribute('aria-busy', 'true');
  submit.setAttribute('aria-label', loadingLabel);
  submit.classList.add('topeak-btn--loading');

  if (input instanceof HTMLInputElement) {
    input.setAttribute('aria-busy', 'true');
  }
}

/**
 * @param {string} type
 */
function showFooterFeedback(type) {
  const feedback = document.querySelector(`[data-newsletter-footer-feedback="${type}"]`);
  const input = document.querySelector('.topeak-footer__newsletter-input');

  if (!(feedback instanceof HTMLElement)) return;

  feedback.hidden = false;

  if (input instanceof HTMLInputElement) {
    if (type === 'error') {
      input.setAttribute('aria-invalid', 'true');
      input.setAttribute('aria-describedby', feedback.id);
      input.focus();
    } else {
      input.setAttribute('aria-describedby', feedback.id);
    }
  }

  if (type === 'success') {
    requestAnimationFrame(() => {
      feedback.focus();
    });
  }
}

/**
 * Removes customer_posted from the URL so success state does not persist on refresh.
 */
function clearCustomerPostedFromUrl() {
  const url = new URL(window.location.href);

  if (!url.searchParams.has('customer_posted')) return;

  url.searchParams.delete('customer_posted');
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
}

/**
 * Applies post-submit success/error UI after a full-page customer form reload.
 *
 * @param {HTMLElement | null} popupElement
 */
export function handleNewsletterPageFeedback(popupElement) {
  if (pageFeedbackHandled) return;

  pageFeedbackHandled = true;

  const posted = isCustomerPosted();
  const source = consumeSubmitSource();

  if (!source) {
    if (posted) {
      clearCustomerPostedFromUrl();
    }

    return;
  }

  const popupHasError = Boolean(
    popupElement?.querySelector('.topeak-newsletter-popup__message--error')
  );

  if (posted && shouldShowPopupSuccessForSource(source) && popupElement instanceof HTMLElement) {
    popupElement.dispatchEvent(
      new CustomEvent('topeak-newsletter:show-success', { bubbles: false })
    );
  }

  if (!posted && source === NewsletterSubmitSource.POPUP && popupHasError && popupElement instanceof HTMLElement) {
    popupElement.dispatchEvent(
      new CustomEvent('topeak-newsletter:show-error', { bubbles: false })
    );
  }

  if (posted && source === NewsletterSubmitSource.FOOTER) {
    showFooterFeedback('success');
  }

  if (!posted && source === NewsletterSubmitSource.FOOTER) {
    showFooterFeedback('error');
  }

  if (posted) {
    clearCustomerPostedFromUrl();
  }
}

initNewsletterSubmitTracking();
